/**
 * Theme bridge to dz-shared-styles. Values are CSS custom property
 * references — the widget root sets `color-data` and inherits the rest from
 * the host page (or src/styles/dev-fallback.scss in standalone dev).
 *
 * Keep using `theme.colors.primary` etc. throughout the codebase; each
 * styled-component string interpolates a `var(--…)` ref and the browser
 * resolves it at render time.
 *
 * NOTE: chart libraries (recharts) accept these as `fill`/`stroke` SVG
 * attributes, where modern browsers resolve var() correctly. For places
 * that need raw hex (color math, gradient stop interpolation), use the
 * literal constants in `utils/colors` instead.
 */

export const theme = {
  colors: {
    primary: 'var(--primary-color)',
    secondary: 'var(--color-text-default)',
    background: 'var(--color-bg-surface)',
    // Cards/surfaces sit on the same beige page bg per shared design — visual
    // separation comes from --shadow-s on `.tile`, not a different fill.
    surface: 'var(--color-bg-surface)',
    text: {
      primary: 'var(--color-text-default)',
      secondary: 'var(--color-text-muted)',
      muted: 'var(--color-text-muted)',
    },
    border: 'var(--color-bg-muted)',
    error: '#F44336',
    success: '#2E7D32',
    warning: '#F57C00',
  },
  fontFamily: 'var(--serif-stack)',
  fontFamilySans: 'var(--sans-stack)',
  fontSize: {
    xs: '0.75rem',
    // Bumped from the shared sans-s (0.8125rem) to sans-m (1rem). The widget is
    // embedded on a 13px-rooted host (dz-shared-styles), where sans-s renders
    // ~10.6px — too small. sans-m yields 13px there, the intended caption size.
    sm: '1rem',
    md: '1rem',
    lg: '1.25rem',
    xl: '1.875rem',
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  borderRadius: {
    sm: 'var(--radius-s)',
    md: 'var(--radius-m)',
    lg: 'var(--radius-l)',
    xl: 'var(--radius-l)',
  },
  shadow: {
    sm: 'var(--shadow-s)',
    md: 'var(--shadow-m)',
    lg: 'var(--shadow-l)',
  },
  spacing: {
    // Mapped to dz-shared-styles space() scale (3px base):
    //   xs ≈ 3px, sm ≈ 9px, md ≈ 15px, lg ≈ 24px, xl ≈ 30px
    // Closest matches to the previous 4/8/16/24/32 scale; visual diff is
    // within a couple of pixels per slot.
    xs: 'var(--space-1)',
    sm: 'var(--space-3)',
    md: 'var(--space-5)',
    lg: 'var(--space-8)',
    xl: 'var(--space-10)',
  },
} as const;

export type Theme = typeof theme;
